import { useState } from "react";
import { ApiError, api } from "../lib/api.ts";
import { proposeAndApply } from "../lib/changes.ts";
import { relativeDate } from "../lib/format.ts";
import { navigate } from "../lib/router.ts";
import { useGet, useSession } from "../lib/session.tsx";
import type { CollectionDef, FieldDef, RoleDef, WorkflowAction, WorkflowDef, WorkflowRun, WorkflowTrigger } from "../lib/types.ts";
import { WEEKDAYS, actionText, triggerText } from "../lib/workflows.ts";

const TRIGGERS: { value: WorkflowTrigger["type"]; label: string }[] = [
  { value: "record.created", label: "Registro criado" },
  { value: "record.updated", label: "Registro alterado" },
  { value: "record.deleted", label: "Registro apagado" },
  { value: "schedule", label: "Agenda" },
  { value: "manual", label: "Botão (manual)" },
];

/** Automations of the store. Like any structure, they change by changeset (risk "sensível"). */
export function Workflows() {
  const s = useSession();
  const list = useGet<{ workflows: WorkflowDef[]; nextRuns: Record<string, string> }>("/v1/workflows");
  const runs = useGet<{ runs: WorkflowRun[] }>("/v1/workflows/runs?limit=30");
  const [editing, setEditing] = useState<WorkflowDef | "new" | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  if (editing) {
    return (
      <WorkflowEditor
        workflow={editing === "new" ? null : editing}
        taken={(list.data?.workflows ?? []).map((w) => w.name)}
        onDone={() => {
          setEditing(null);
          list.reload();
          s.reloadStructure();
        }}
      />
    );
  }

  async function runNow(w: WorkflowDef) {
    setBusy(w.name);
    try {
      const { run } = await api<{ run: WorkflowRun }>("POST", `/v1/workflows/${w.name}/run`, {});
      s.toast(run.status === "ok" ? `${w.label}: feito.` : `${w.label}: falhou.`);
      runs.reload();
    } catch (e) {
      s.toast(e instanceof ApiError ? e.message : "Não foi possível rodar.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="stack" style={{ maxWidth: 900 }}>
      <div className="topbar">
        <h1>Workflows</h1>
        <span className="spacer" />
        {s.canBuild && (
          <button className="btn primary" onClick={() => setEditing("new")}>
            Novo workflow
          </button>
        )}
      </div>
      <div className="card stack">
        <p style={{ margin: 0 }}>
          Um workflow faz algo sozinho quando um registro é criado, alterado ou apagado, num horário, ou num botão: cria registros, muda campos, baixa estoque, marca contas atrasadas.
        </p>
        <p className="muted" style={{ margin: 0 }}>
          Workflows agem com acesso total, por isso só Dono e Construtor os criam e cada mudança passa por changeset. Botões com registro aparecem nos detalhes do registro.
        </p>
      </div>
      {list.error && <p className="error">{list.error.message}</p>}
      <div className="card list">
        <strong>Workflows desta loja</strong>
        {list.data?.workflows.map((w) => (
          <div key={w.name} className="list-item" style={{ alignItems: "flex-start" }}>
            <div className="stack" style={{ gap: 2 }}>
              <span style={{ fontWeight: 600 }}>
                {w.label} <code className="muted">{w.name}</code> {!w.enabled && <span className="chip gray">desligado</span>}
              </span>
              {w.description && <span className="muted">{w.description}</span>}
              <span style={{ fontSize: 12.5 }}>{triggerText(w.trigger)}</span>
              {w.actions.map((a, i) => (
                <span key={i} style={{ fontSize: 12.5 }}>
                  {i + 1}. {actionText(a)}
                </span>
              ))}
              {list.data?.nextRuns[w.name] && (
                <span className="muted" style={{ fontSize: 12.5 }}>
                  Próxima execução: {relativeDate(list.data.nextRuns[w.name]!)}
                </span>
              )}
            </div>
            <span className="spacer" />
            {w.enabled && w.trigger.type === "manual" && !w.trigger.collection && (
              <button className="btn sm" disabled={!!busy} onClick={() => runNow(w)}>
                {w.trigger.label ?? "Rodar"}
              </button>
            )}
            {s.canBuild && (
              <button className="btn sm" onClick={() => setEditing(w)}>
                Editar
              </button>
            )}
          </div>
        ))}
        {list.data && !list.data.workflows.length && <p className="muted" style={{ margin: 0 }}>Nenhum workflow ainda. Peça à sua IA ou crie aqui.</p>}
      </div>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Últimas execuções</h3>
        {runs.error && <p className="error">{runs.error.message}</p>}
        {runs.data && !runs.data.runs.length && <p className="muted">Nada rodou ainda.</p>}
        {!!runs.data?.runs.length && (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Workflow</th>
                  <th>Resultado</th>
                </tr>
              </thead>
              <tbody>
                {runs.data.runs.map((r) => (
                  <tr key={r.id}>
                    <td>{relativeDate(r.startedAt)}</td>
                    <td>
                      {r.workflow} <span className="muted">· {TRIGGERS.find((t) => t.value === r.trigger)?.label}</span>
                    </td>
                    <td style={{ whiteSpace: "normal" }}>
                      {r.status === "ok" ? (
                        <span>
                          <span className="chip green">ok</span> {stepsText(r)}
                        </span>
                      ) : (
                        <span>
                          <span className="chip red">erro</span> {r.error}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function stepsText(r: WorkflowRun): string {
  const created = r.steps.filter((x) => x.created).length;
  const updated = r.steps.reduce((n, x) => n + (x.updated ?? 0), 0);
  const parts = [];
  if (created) parts.push(`${created} criado(s)`);
  if (updated) parts.push(`${updated} alterado(s)`);
  return parts.join(", ") || "nada a fazer";
}

// --- Editor --------------------------------------------------------------------

interface Draft {
  name: string;
  label: string;
  description: string;
  enabled: boolean;
  trigger: WorkflowTrigger;
  actions: WorkflowAction[];
}

function WorkflowEditor({ workflow, taken, onDone }: { workflow: WorkflowDef | null; taken: string[]; onDone: () => void }) {
  const s = useSession();
  const roles = useGet<{ roles: RoleDef[] }>("/v1/roles");
  const first = s.collections[0]?.name ?? "";
  const [d, setD] = useState<Draft>(() =>
    workflow
      ? { name: workflow.name, label: workflow.label, description: workflow.description ?? "", enabled: workflow.enabled, trigger: workflow.trigger, actions: workflow.actions }
      : { name: "", label: "", description: "", enabled: true, trigger: { type: "record.created", collection: first }, actions: [{ type: "record.create", collection: first, values: {} }] },
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));

  async function save() {
    setBusy(true);
    setErr(null);
    const ops = workflow
      ? [
          {
            op: "workflow.update",
            workflow: workflow.name,
            set: {
              ...(d.name !== workflow.name ? { name: d.name } : {}),
              label: d.label || undefined,
              description: d.description.trim() || null,
              enabled: d.enabled,
              trigger: d.trigger,
              actions: d.actions,
            },
          },
        ]
      : [
          {
            op: "workflow.create",
            name: d.name,
            ...(d.label ? { label: d.label } : {}),
            ...(d.description.trim() ? { description: d.description.trim() } : {}),
            enabled: d.enabled,
            trigger: d.trigger,
            actions: d.actions,
          },
        ];
    try {
      const { changeset, applied } = await proposeAndApply(`${workflow ? "Alterar" : "Criar"} workflow ${d.name}`, ops);
      if (applied) {
        s.toast("Workflow salvo.");
        onDone();
      } else navigate(`/changesets/${changeset.id}`);
    } catch (e) {
      setErr(e instanceof ApiError ? [e.message, ...e.details.map((x) => `${x.path.slice(2).join(".")}: ${x.message}`)].join(" ") : "Não foi possível salvar.");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!workflow || !confirm(`Remover o workflow ${workflow.label}?`)) return;
    setBusy(true);
    try {
      await proposeAndApply(`Remover workflow ${workflow.name}`, [{ op: "workflow.remove", workflow: workflow.name }]);
      s.toast("Workflow removido.");
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erro");
      setBusy(false);
    }
  }

  const nameOk = /^[a-z][a-z0-9_]{0,62}$/.test(d.name) && (d.name === workflow?.name || !taken.includes(d.name));
  const recordTrigger = d.trigger.type === "schedule" ? undefined : d.trigger.collection;

  return (
    <div className="stack" style={{ maxWidth: 900 }}>
      <div className="topbar">
        <button className="linkish" onClick={onDone}>
          ← Workflows
        </button>
      </div>
      <div className="topbar">
        <h1>{workflow ? `Workflow ${workflow.label}` : "Novo workflow"}</h1>
        <span className="spacer" />
        {workflow && (
          <button className="btn danger" onClick={remove} disabled={busy}>
            Remover
          </button>
        )}
        <button className="btn primary" onClick={save} disabled={busy || !nameOk || !d.actions.length}>
          Salvar
        </button>
      </div>
      {err && <p className="error">{err}</p>}
      <div className="card form">
        <label className={`fld ${d.name && !nameOk ? "bad" : ""}`}>
          Nome (identificador)
          <input value={d.name} onChange={(e) => set({ name: e.target.value.toLowerCase() })} placeholder="baixa_estoque" />
          {d.name && !nameOk && <span className="err">Letras minúsculas, números e _, começando por letra; sem repetir um workflow existente.</span>}
        </label>
        <label className="fld">
          Rótulo
          <input value={d.label} onChange={(e) => set({ label: e.target.value })} placeholder="Baixa de estoque" />
        </label>
        <label className="fld wide">
          Descrição
          <input value={d.description} onChange={(e) => set({ description: e.target.value })} placeholder="Tira do estoque o que foi vendido" />
        </label>
        <label className="checkline">
          <input type="checkbox" checked={d.enabled} onChange={(e) => set({ enabled: e.target.checked })} /> Ligado
        </label>
      </div>
      <div className="card stack">
        <h3 style={{ margin: 0 }}>Quando</h3>
        <TriggerEditor value={d.trigger} collections={s.collections} roles={roles.data?.roles ?? []} onChange={(trigger) => set({ trigger })} />
      </div>
      <div className="card stack">
        <h3 style={{ margin: 0 }}>Faz, em ordem</h3>
        <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
          Valores aceitam expressões: <code>{'{"$field": "campo"}'}</code> (campo do registro do gatilho), <code>{'{"$current": "estoque"}'}</code> (registro sendo alterado),{" "}
          <code>{'{"$today": 0}'}</code>, <code>{'{"$sub": [a, b]}'}</code> e textos com <code>{"{{campo}}"}</code>. Se uma ação falhar, nada da execução fica.
        </p>
        {d.actions.map((a, i) => (
          <ActionEditor
            key={i}
            n={i + 1}
            value={a}
            collections={s.collections}
            canUseTrigger={!!recordTrigger && d.trigger.type !== "record.deleted"}
            triggerCollection={recordTrigger}
            onChange={(v) => set({ actions: d.actions.map((x, j) => (j === i ? v : x)) })}
            onRemove={() => set({ actions: d.actions.filter((_, j) => j !== i) })}
          />
        ))}
        <div>
          <button className="btn sm" disabled={d.actions.length >= 20} onClick={() => set({ actions: [...d.actions, { type: "record.create", collection: first, values: {} }] })}>
            + Ação
          </button>
        </div>
      </div>
    </div>
  );
}

function CollectionSelect({ value, collections, onChange, empty }: { value: string | undefined; collections: CollectionDef[]; onChange: (v: string | undefined) => void; empty?: string }) {
  return (
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value || undefined)}>
      {empty !== undefined && <option value="">{empty}</option>}
      {collections.map((c) => (
        <option key={c.name} value={c.name}>
          {c.label}
        </option>
      ))}
    </select>
  );
}

function TriggerEditor({ value: t, collections, roles, onChange }: { value: WorkflowTrigger; collections: CollectionDef[]; roles: RoleDef[]; onChange: (t: WorkflowTrigger) => void }) {
  const first = collections[0]?.name ?? "";
  const change = (type: WorkflowTrigger["type"]) => {
    const col = t.type !== "schedule" && t.collection ? t.collection : first;
    if (type === "schedule") onChange({ type, every: "day", at: "08:00" });
    else if (type === "manual") onChange({ type, collection: col });
    else onChange({ type, collection: col } as WorkflowTrigger);
  };
  const c = t.type !== "schedule" && t.collection ? collections.find((x) => x.name === t.collection) : undefined;
  return (
    <div className="form">
      <label className="fld">
        Gatilho
        <select value={t.type} onChange={(e) => change(e.target.value as WorkflowTrigger["type"])}>
          {TRIGGERS.map((x) => (
            <option key={x.value} value={x.value}>
              {x.label}
            </option>
          ))}
        </select>
      </label>
      {(t.type === "record.created" || t.type === "record.updated" || t.type === "record.deleted") && (
        <>
          <label className="fld">
            Coleção
            <CollectionSelect value={t.collection} collections={collections} onChange={(v) => onChange({ ...t, collection: v ?? first, ...(t.type === "record.updated" ? { fields: undefined } : {}), filter: undefined })} />
          </label>
          {t.type === "record.updated" && c && (
            <div className="fld wide">
              Só quando mudar (vazio = qualquer campo)
              <div className="checks">
                {c.fields.map((f) => {
                  const on = t.fields?.includes(f.name) ?? false;
                  return (
                    <label key={f.name} className={on ? "on" : ""}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => {
                          const next = on ? (t.fields ?? []).filter((x) => x !== f.name) : [...(t.fields ?? []), f.name];
                          onChange({ ...t, fields: next.length ? next : undefined });
                        }}
                      />
                      {f.label}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          <JsonField label="Só se o registro casar com este filtro (JSON, opcional)" placeholder='{"status": "pago"}' value={t.filter} onChange={(filter) => onChange({ ...t, filter })} />
        </>
      )}
      {t.type === "schedule" && (
        <>
          <label className="fld">
            Frequência
            <select
              value={t.every}
              onChange={(e) => {
                const every = e.target.value as "hour" | "day" | "week" | "month";
                onChange(every === "hour" ? { type: "schedule", every, minute: 0 } : every === "week" ? { type: "schedule", every, at: t.at ?? "08:00", weekday: 1 } : every === "month" ? { type: "schedule", every, at: t.at ?? "08:00", day: 1 } : { type: "schedule", every, at: t.at ?? "08:00" });
              }}
            >
              <option value="hour">A cada hora</option>
              <option value="day">Todo dia</option>
              <option value="week">Toda semana</option>
              <option value="month">Todo mês</option>
            </select>
          </label>
          {t.every === "hour" ? (
            <label className="fld">
              Minuto
              <input type="number" min={0} max={59} value={t.minute ?? 0} onChange={(e) => onChange({ ...t, minute: Math.max(0, Math.min(59, Number(e.target.value) || 0)) })} />
            </label>
          ) : (
            <label className="fld">
              Horário (Brasília)
              <input type="time" value={t.at ?? "08:00"} onChange={(e) => onChange({ ...t, at: e.target.value || "08:00" })} />
            </label>
          )}
          {t.every === "week" && (
            <label className="fld">
              Dia da semana
              <select value={t.weekday ?? 1} onChange={(e) => onChange({ ...t, weekday: Number(e.target.value) })}>
                {WEEKDAYS.map((w, i) => (
                  <option key={w} value={i}>
                    {w}
                  </option>
                ))}
              </select>
            </label>
          )}
          {t.every === "month" && (
            <label className="fld">
              Dia do mês
              <input type="number" min={1} max={28} value={t.day ?? 1} onChange={(e) => onChange({ ...t, day: Math.max(1, Math.min(28, Number(e.target.value) || 1)) })} />
            </label>
          )}
        </>
      )}
      {t.type === "manual" && (
        <>
          <label className="fld">
            Num registro de
            <CollectionSelect value={t.collection} collections={collections} empty="(nenhum: botão na lista de workflows)" onChange={(v) => onChange({ ...t, collection: v })} />
          </label>
          <label className="fld">
            Texto do botão
            <input value={t.label ?? ""} onChange={(e) => onChange({ ...t, label: e.target.value || undefined })} placeholder="Cancelar pedido" />
          </label>
          {roles.length > 0 && (
            <div className="fld wide">
              Também podem usar (Dono, Construtor e Operador sempre podem)
              <div className="checks">
                {roles.map((r) => {
                  const on = t.roles?.includes(r.name) ?? false;
                  return (
                    <label key={r.name} className={on ? "on" : ""}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => {
                          const next = on ? (t.roles ?? []).filter((x) => x !== r.name) : [...(t.roles ?? []), r.name];
                          onChange({ ...t, roles: next.length ? next : undefined });
                        }}
                      />
                      {r.label}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ActionEditor({
  n,
  value: a,
  collections,
  canUseTrigger,
  triggerCollection,
  onChange,
  onRemove,
}: {
  n: number;
  value: WorkflowAction;
  collections: CollectionDef[];
  canUseTrigger: boolean;
  triggerCollection: string | undefined;
  onChange: (a: WorkflowAction) => void;
  onRemove: () => void;
}) {
  const c = collections.find((x) => x.name === a.collection);
  const onTrigger = a.type === "record.update" && !a.filter;
  return (
    <div className="diff-item" style={{ borderLeftColor: "var(--line)" }}>
      <div className="form">
        <label className="fld">
          Ação {n}
          <select
            value={a.type}
            onChange={(e) =>
              onChange(
                e.target.value === "record.create"
                  ? { type: "record.create", collection: a.collection, values: a.values }
                  : canUseTrigger && triggerCollection
                    ? { type: "record.update", collection: triggerCollection, values: {} }
                    : { type: "record.update", collection: a.collection, filter: {}, values: {} },
              )
            }
          >
            <option value="record.create">Criar registro</option>
            <option value="record.update">Alterar registros</option>
          </select>
        </label>
        {a.type === "record.update" && (
          <label className="fld">
            Quais
            <select
              value={onTrigger ? "trigger" : "filter"}
              onChange={(e) =>
                onChange(e.target.value === "trigger" && triggerCollection ? { type: "record.update", collection: triggerCollection, values: {} } : { ...a, filter: a.filter ?? {} })
              }
            >
              {canUseTrigger && <option value="trigger">O registro do gatilho</option>}
              <option value="filter">Os que casam com um filtro</option>
            </select>
          </label>
        )}
        {!onTrigger && (
          <label className="fld">
            Coleção
            <CollectionSelect value={a.collection} collections={collections} onChange={(v) => onChange({ ...a, collection: v ?? a.collection, values: {} })} />
          </label>
        )}
        {a.type === "record.update" && !onTrigger && (
          <JsonField label="Filtro (JSON; vazio = todos, até 500 registros)" placeholder='{"id": {"$field": "produto"}}' value={a.filter} onChange={(filter) => onChange({ ...a, filter: filter ?? {} })} />
        )}
      </div>
      {c && <ValuesEditor fields={c.fields} values={a.values} onChange={(values) => onChange({ ...a, values } as WorkflowAction)} />}
      <div style={{ marginTop: 6 }}>
        <button className="linkish" onClick={onRemove}>
          remover ação
        </button>
      </div>
    </div>
  );
}

const TEXTUAL = new Set(["text", "longText", "email", "phone", "cpf", "cnpj", "cep", "select", "date", "datetime", "relation", "currency"]);

/** What the person typed, as the value to store: JSON for expressions, numbers and lists; text otherwise. */
function parseValue(f: FieldDef, text: string): unknown {
  const t = text.trim();
  if (t.startsWith("{") || t.startsWith("[") || !TEXTUAL.has(f.interface)) {
    try {
      return JSON.parse(t);
    } catch {
      return text;
    }
  }
  return text;
}

function showValue(v: unknown): string {
  return typeof v === "string" && !v.trim().startsWith("{") && !v.trim().startsWith("[") ? v : JSON.stringify(v);
}

function ValuesEditor({ fields, values, onChange }: { fields: FieldDef[]; values: Record<string, unknown>; onChange: (v: Record<string, unknown>) => void }) {
  const [texts, setTexts] = useState<Record<string, string>>(() => Object.fromEntries(Object.entries(values).map(([k, v]) => [k, showValue(v)])));
  const used = Object.keys(texts);
  const free = fields.filter((f) => !used.includes(f.name) && f.interface !== "attachment");
  const update = (next: Record<string, string>) => {
    setTexts(next);
    onChange(Object.fromEntries(Object.entries(next).map(([k, t]) => [k, parseValue(fields.find((f) => f.name === k)!, t)])));
  };
  return (
    <div className="stack" style={{ gap: 6, marginTop: 8 }}>
      {used.map((name) => {
        const f = fields.find((x) => x.name === name);
        return (
          <div key={name} className="row" style={{ flexWrap: "nowrap" }}>
            <span style={{ minWidth: 140 }}>{f?.label ?? name}</span>
            <input
              style={{ flex: 1, fontFamily: "var(--f-mono)", fontSize: 12 }}
              value={texts[name]}
              placeholder={f?.interface === "number" ? '10 ou {"$field": "quantidade"}' : 'texto, {{campo}} ou {"$field": "campo"}'}
              onChange={(e) => update({ ...texts, [name]: e.target.value })}
            />
            <button
              className="linkish"
              onClick={() => {
                const { [name]: _, ...rest } = texts;
                update(rest);
              }}
            >
              tirar
            </button>
          </div>
        );
      })}
      {free.length > 0 && (
        <div>
          <select className="input" style={{ width: "auto" }} value="" onChange={(e) => e.target.value && update({ ...texts, [e.target.value]: "" })} aria-label="Adicionar campo">
            <option value="">+ campo…</option>
            {free.map((f) => (
              <option key={f.name} value={f.name}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

function JsonField({ label, placeholder, value, onChange }: { label: string; placeholder: string; value: Record<string, unknown> | undefined; onChange: (v: Record<string, unknown> | undefined) => void }) {
  const [text, setText] = useState(value && Object.keys(value).length ? JSON.stringify(value) : "");
  const [bad, setBad] = useState(false);
  return (
    <label className={`fld wide ${bad ? "bad" : ""}`}>
      {label}
      <input
        style={{ fontFamily: "var(--f-mono)", fontSize: 12 }}
        placeholder={placeholder}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (!e.target.value.trim()) {
            setBad(false);
            return onChange(undefined);
          }
          try {
            const v = JSON.parse(e.target.value) as unknown;
            const ok = !!v && typeof v === "object" && !Array.isArray(v);
            setBad(!ok);
            if (ok) onChange(v as Record<string, unknown>);
          } catch {
            setBad(true);
          }
        }}
      />
      {bad && <span className="err">JSON inválido</span>}
    </label>
  );
}
